import axios from 'axios'
import { Message, Loading } from 'element-ui'

// --- 优化点：防止多个错误同时弹出，造成刷屏 ---
let messageInstance = null
const showMessage = (msg) => {
  if (messageInstance) {
    messageInstance.close() // 如果有弹窗，先关闭上一个
  }
  messageInstance = Message.error({
    message: msg,
    duration: 3000
  })
}

// 1. 创建axios实例
const service = axios.create({
  baseURL: 'https://api.inteforma.com', // 建议后续放入 process.env.VUE_APP_BASE_API
  // baseURL: 'http://192.168.5.94:8098', // 建议后续放入 process.env.VUE_APP_BASE_API
  timeout: 30000, // 10秒通常够用了
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

// 存储当前请求的取消函数
const pendingRequests = new Map()

// 生成请求唯一标识
const generateKey = (config) => {
  const { method, url, params, data } = config
  return [method, url, JSON.stringify(params), JSON.stringify(data)].join('&')
}

// 2. 请求拦截器
service.interceptors.request.use(
  (config) => {
    // --- 【核心修改点开始】 ---
    // 判断 data 是否为 FormData 类型
    if (config.data instanceof FormData) {
      // 关键：删除默认的 Content-Type
      // 让浏览器自动生成 'multipart/form-data; boundary=----WebKitFormBoundary...'
      delete config.headers['Content-Type']
    }
    // --- 【核心修改点结束】 ---

    // 取消重复请求
    const requestKey = generateKey(config)
    if (pendingRequests.has(requestKey)) {
      pendingRequests.get(requestKey)() 
      pendingRequests.delete(requestKey)
    }
    const source = axios.CancelToken.source()
    config.cancelToken = source.token
    pendingRequests.set(requestKey, source.cancel)

    // 添加token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // ... (省略 loading 逻辑) ...

    return config
  },
  (error) => {
    console.error(error)
    return Promise.reject(error)
  }
)

// 3. 响应拦截器
service.interceptors.response.use(
  (response) => {
    // 清除请求标识
    const requestKey = generateKey(response.config)
    pendingRequests.delete(requestKey)
    // 关闭Loading
    if (response.config.loadingInstance) {
      response.config.loadingInstance.close()
    }

    const res = response.data
    // 兼容后端状态码
    const successCode = [200, 20000]
    
    // 如果 code 不在成功列表中
    if (!successCode.includes(res.code)) {
      // 401 单独处理
      if (res.code === 401) {
        showMessage('登录已过期，请重新登录')
        localStorage.removeItem('token')
        // 建议：此处可以使用 router.push('/login')
        // location.reload() // 或者刷新页面
      } else {
        // 其他错误使用 Message 提示
        showMessage(res.msg || '系统接口异常')
      }
      
      // 返回 reject，这样你在组件里调用接口时，.then() 不会执行，会进 .catch()
      // 但因为我们已经在上面 showMessage 了，组件里的 .catch 可以留空或者做逻辑处理
      return Promise.reject(new Error(res.msg || 'Error'))
    }
    
    // 请求成功，返回数据
    return res
  },
  (error) => {
    // 清除请求标识和Loading
    if (error.config) {
      const requestKey = generateKey(error.config)
      pendingRequests.delete(requestKey)
      if (error.config.loadingInstance) {
        error.config.loadingInstance.close()
      }
    }

    // 如果是取消请求，不报错，不提示
    if (axios.isCancel(error)) {
      return new Promise(() => {}) // 返回一个永远不resolve的promise，中断链条
    }

    let errMsg = ''
    if (error.message.includes('timeout')) {
      errMsg = '请求超时，请检查网络连接'
    } else if (error.response) {
      const status = error.response.status
      switch (status) {
        case 404:
          errMsg = '接口地址不存在 (404)'
          break
        case 500:
          errMsg = '服务器内部错误 (500)'
          break
        case 403:
          errMsg = '无权访问 (403)'
          break
        default:
          errMsg = error.response.data?.msg || `连接错误 (${status})`
      }
    } else {
      errMsg = '网络连接失败，请检查网络'
    }

    // 使用封装的提示函数
    showMessage(errMsg)
    return Promise.reject(error)
  }
)

export default service